import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, AlertCircle } from "lucide-react";

const ConfirmActionDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  loading = false,
  loadingText = "Processing...",
  success = false,
  successText = "Action completed successfully",
  error = null,
  errorText = null,
  variant = "default",
}) => {
  const getConfirmButtonClass = () => {
    if (variant === "destructive") {
      return "bg-red-600 hover:bg-red-700 text-white";
    }
    return "";
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center gap-4 py-8">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20" />
            <div className="absolute inset-0 animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
          </div>
          <p className="text-center text-muted-foreground font-medium">
            {loadingText}
          </p>
        </div>
      );
    }

    if (success) {
      return (
        <div className="flex flex-col items-center gap-4 py-8">
          <div className="rounded-full bg-green-100 dark:bg-green-900/20 p-3">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
          <p className="text-center font-medium text-green-600 dark:text-green-400">
            {successText}
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col gap-4 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-red-100 dark:bg-red-900/20 p-2">
              <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <p className="font-semibold text-red-600 dark:text-red-400">
              Error Occurred
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            {errorText || error}
          </p>
        </div>
      );
    }

    return (
      <div className="flex gap-3">
        {variant === "destructive" && (
          <div className="rounded-full bg-red-100 dark:bg-red-900/20 p-2 h-fit">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
        )}
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    );
  };

  const shouldShowFooter = !loading && !success;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!loading) {
          onOpenChange(newOpen);
        }
      }}
    >
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription asChild>
          <div>{renderContent()}</div>
        </AlertDialogDescription>
        {shouldShowFooter && (
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>
              {cancelText}
            </AlertDialogCancel>
            {!error && (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  onConfirm();
                }}
                disabled={loading}
                className={getConfirmButtonClass()}
                variant={variant === "destructive" ? "destructive" : "default"}
              >
                {confirmText}
              </Button>
            )}
          </AlertDialogFooter>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmActionDialog;